<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="800">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="saveConfig">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>{{ method === 'post' ? "Create" : "Update" }} Device Config File</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0 d-flex justify-space-between" cols="12">
                            <v-switch v-model="formData['download_needed']" color="primary" density="compact"
                                      label="Download Needed"/>
                            <v-switch v-model="formData['downloading_queued']" color="primary" density="compact"
                                      label="Download Queued"/>
                        </v-col>
                        <v-col cols="12">
                            <v-select v-model="formData['master_config_id']" :items="master_configs"
                                      :loading="!master_configs.length" :rules="[vRules.required]"
                                      item-title="name" item-value="id" label="Master Config">
                            </v-select>
                            <v-select v-if="!device" v-model="formData['device_id']" :items="devices"
                                      :rules="[vRules.required]" item-title="slno" item-value="id" label="Device">
                            </v-select>
                        </v-col>

                        <v-col cols="12">
                            <prism-editor
                                v-model="content"
                                :highlight="highlighter"
                                class="my-editor"
                                line-numbers
                            ></prism-editor>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import "vue-prism-editor/dist/prismeditor.min.css";

import {ref, reactive, onMounted, computed} from "vue";
import {send} from "@/composables/apiClient";
import {PrismEditor} from "vue-prism-editor";
import {highlight, languages} from "prismjs";
import {useLoadingStore} from "@/stores/loadingStore";
import {useSnackbarStore} from "@/stores/snackBarStore";


const loadingStore = useLoadingStore();
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    method: {
        type: String,
        default: 'post',
    },
    device: {
        type: Number,
        default: null,
    },
    config: {
        default: 0,
        type: Number,
    }
});
const formData = reactive({
    valid: false,
    file_content: null,
    master_config_id: null,
    download_needed: false,
    downloading_queued: false,
    device_id: props.device ?? null,
});
const emits = defineEmits(['update:modelValue', 'addConfig', 'updateConfig']);
const content = ref("# Config values\n");
const dialogSwitch = ref(props.modelValue);
const master_configs = ref([]);
const method = ref(props.method);
const config = ref(null);
const devices = ref([]);
const errors = ref([]);

const selectedMasterConfig = computed(() => {
    if (formData['master_config_id']) {
        return master_configs.value.find((item) => item.id === formData['master_config_id'])
    }
    return null
});
const selectedDevice = computed(() => {
    if (formData['device_id']) {
        return devices.value.find((item) => item.id === formData['device_id'])
    }
    return null
});
const highlighter = (content) => highlight(content, languages.javascript, 'javascript');

onMounted(() => {
    if (props.config || props.device) {
        fetchConfigDetails()
    }
    fetchMasterConfigs()
})
const fetchConfigDetails = () => {
    loadingStore.showLoading(`${props.config ? 'Updating' : 'Creating'}...`, 'Please wait while we save your changes');
    return send('get', `${props.config ? 'admin/device-configs/' + props.config : 'admin/device-configs/' + props.device + '/device'}`)
        .then((response) => {
            if (response.data.data) {
                method.value = 'put';
                config.value = response.data.data;
                content.value = config.value.file_content;
                formData['device_id'] = config.value.device_id;
                formData['master_config_id'] = config.value.master_config_id;
                formData['download_needed'] = !!config.value.download_needed;
                formData['downloading_queued'] = !!config.value.downloading_queued;
            }
        }).catch((error) => {
            snackbar.showSnackbar('Config details could not be loaded', 'error')
        }).finally(() => loadingStore.hideLoading());
}
const fetchMasterConfigs = () => {
    return send('post', `admin/device-configs-list`, {master: 1})
        .then((response) => {
            master_configs.value = response.data.data;
        }).catch((error) => {
            snackbar.showSnackbar('Master config list could not be loaded', 'error')
        }).finally(() => {
            if (!props.device && formData['master_config_id']) {
                fetchDevices()
            }
        })
}
const fetchDevices = () => {
    // A master config binds to many SKUs (pivot), so the list carries product_sku_ids.
    const skuIds = selectedMasterConfig.value?.product_sku_ids ?? [];
    if (!skuIds.length) {
        devices.value = [];
        return;
    }
    send('post', `product-sku/devices`, {product_sku_id: skuIds})
        .then((response) => {
            devices.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Devices could not be loaded', 'error')
        })
}
const saveConfig = () => {
    errors.value = [];
    if (content.value === "" || content.value === "# Config values\n") {
        alert("File content shouldn't be empty")
        return false
    }
    formData['file_content'] = content.value;
    send(method.value, `admin/device-configs${method.value === 'put' ? '/' + config.value.id : ''}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits(method.value === 'put' ? 'updateConfig' : 'addConfig', {
                    slno: selectedDevice.value?.slno ?? null,
                    master_config: selectedMasterConfig.value.name,
                    ...response.data.data
                });
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
</script>

<style>
.my-editor {
    color: #ccc;
    height: 100%;
    padding: 5px;
    font-size: 14px;
    line-height: 1.5;
    min-height: 300px;
    max-height: 500px;
    background: #2d2d2d;
    font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
}

.my-editor .prism-editor__container {
    padding-left: 5px;
    background: #434343;
}

.my-editor .prism-editor__textarea {
    margin-left: 5px;
}

.my-editor .prism-editor__textarea:focus-visible, .my-editor .prism-editor__textarea:focus {
    outline: none;
}
</style>
